import { Component } from "@theme/component";

// Only define the component if it hasn't been defined yet
if (!customElements.get("bundle-collection-component")) {
  /**
   * Bundle Collection Block
   * Handles interactions for bundle collection product displays
   *
   * A custom element that dispatches events when products are clicked.
   * The parent bundle-showcase component listens for these events and
   * handles the section updates.
   *
   * @extends {Component}
   */
  class BundleCollectionComponent extends Component {
    constructor() {
      super();
      this.productChangedHandler = null;
      this.swiperCheckInterval = null;
    }

    connectedCallback() {
      super.connectedCallback();
      this.#setupClickHandler();
      this.#initCarousel();

      // Set initial active state after a small delay to ensure parent is ready
      requestAnimationFrame(() => {
        this.#updateActiveState();
      });

      // Listen for product change events from parent
      this.productChangedHandler = this.#handleProductChanged.bind(this);
      this.addEventListener("bundle:product-changed", this.productChangedHandler);
    }

    disconnectedCallback() {
      super.disconnectedCallback();

      if (this.productChangedHandler) {
        this.removeEventListener("bundle:product-changed", this.productChangedHandler);
      }

      if (this.swiperCheckInterval) {
        clearInterval(this.swiperCheckInterval);
        this.swiperCheckInterval = null;
      }
    }

    /**
     * Initialize freeMode + scrollbar carousel below desktop.
     * Relies on window config from the liquid template + theme carousel helper.
     */
    #initCarousel() {
      const carousel = /** @type {HTMLElement | undefined} */ (this.refs?.productsContainer);

      if (!carousel) {
        return;
      }

      if (carousel.classList.contains("swiper-initialized")) {
        return;
      }

      const tryInit = () => {
        if (typeof window.initCarousels === "function") {
          window.initCarousels(".bundle-collection__carousel", false);
          return true;
        }

        if (typeof window.initCarousel === "function") {
          window.initCarousel(carousel);
          return true;
        }

        return false;
      };

      if (tryInit()) {
        return;
      }

      // Wait briefly for carousel.js / Swiper to load
      this.swiperCheckInterval = setInterval(() => {
        if (tryInit() || carousel.classList.contains("swiper-initialized")) {
          clearInterval(this.swiperCheckInterval);
          this.swiperCheckInterval = null;
        }
      }, 100);
    }

    /**
     * Handle product change event from parent
     * @param {CustomEvent} event - Event with new product handle
     */
    #handleProductChanged(event) {
      const productHandle = event.detail?.productHandle;
      if (productHandle) {
        this.#setActiveProduct(productHandle);
      }
    }

    /**
     * Setup click handler for product selection
     */
    #setupClickHandler() {
      const container = /** @type {HTMLElement | undefined} */ (this.refs?.productsContainer);

      if (!container) {
        console.log("[Bundle Collection] Products container ref not found");
        return;
      }

      container.addEventListener(
        "click",
        /** @param {MouseEvent} event */ event => {
          const target = /** @type {HTMLElement} */ (event.target);
          const productElement = target.closest("[data-product-handle]");

          if (!productElement) {
            return;
          }

          const productHandle = /** @type {HTMLElement} */ (productElement).dataset.productHandle;

          if (!productHandle) {
            console.warn("[Bundle Collection] No product handle found");
            return;
          }

          this.#setActiveProduct(productHandle);
          this.dispatchProductSelectedEvent(productHandle);
        }
      );
    }

    /**
     * Update active state based on current product in parent section
     */
    #updateActiveState() {
      const bundleShowcase = /** @type {HTMLElement | null} */ (this.closest("bundle-showcase-component"));
      if (!bundleShowcase) {
        console.warn("[Bundle Collection] Parent bundle-showcase not found");
        return;
      }

      const currentHandle = bundleShowcase.dataset.currentProductHandle;

      if (currentHandle) {
        this.#setActiveProduct(currentHandle);
      } else {
        console.warn("[Bundle Collection] No current product handle found on parent");
      }
    }

    /**
     * Set active product by handle
     * @param {string} productHandle - The product handle to mark as active
     */
    #setActiveProduct(productHandle) {
      const container = /** @type {HTMLElement | undefined} */ (this.refs?.productsContainer);

      if (!container) {
        console.warn("[Bundle Collection] Products container not found");
        return;
      }

      const productElements = container.querySelectorAll("[data-product-handle]");

      let foundActive = false;
      let activeStyleName = "";

      for (const el of productElements) {
        const element = /** @type {HTMLElement} */ (el);
        const handle = element.dataset.productHandle;
        const isActive = handle === productHandle;
        const label = element.querySelector(".bundle-collection__item-label");

        if (isActive) {
          foundActive = true;
          activeStyleName = element.dataset.styleName || "";
        }

        element.setAttribute("aria-pressed", isActive ? "true" : "false");

        if (isActive) {
          element.classList.remove("border-medium-smoke");
          element.classList.add("border-asphalt");
        } else {
          element.classList.remove("border-asphalt");
          element.classList.add("border-medium-smoke");
        }

        if (label) {
          if (isActive) {
            label.classList.remove("text-14-light");
            label.classList.add("text-14-bold");
          } else {
            label.classList.remove("text-14-bold");
            label.classList.add("text-14-light");
          }
        }
      }

      const styleNameEl = /** @type {HTMLElement | undefined} */ (this.refs?.selectedStyleName);
      if (styleNameEl && activeStyleName) {
        styleNameEl.textContent = activeStyleName;
      }

      if (!foundActive) {
        console.warn("[Bundle Collection] Product handle not found in collection:", productHandle);
      }
    }

    /**
     * Dispatch a custom event to notify parent of product selection
     * @param {string} productHandle - The selected product handle
     */
    dispatchProductSelectedEvent(productHandle) {
      const event = new CustomEvent("bundle:product-selected", {
        detail: { productHandle },
        bubbles: true,
        cancelable: true,
      });

      this.dispatchEvent(event);
    }
  }

  // Register the custom element
  customElements.define("bundle-collection-component", BundleCollectionComponent);
}
